<template>
  <Sidebar v-model:visible="showNotificationDetailDialog" class="p-sidebar-md notification-detail-sidebar" position="right" :showCloseIcon="false">
    <div class="notification-detail-content">
      <div class="detail-header">
        <h3 class="detail-title">
          App Notification
          <span class="detail-subtitle">- {{ notificationDetail.venueName ?? 'OneSignal' }}</span>
        </h3>
        
        <div class="venue-logo-container">
          <img class="venue-logo" :src="notificationDetail.venueLogoUrl ?? npLogo" alt="Venue Logo" />
        </div>
      </div>

      <div class="notification-preview">
        <div class="preview-content">
          <div class="notification-title">{{ notificationDetail.title }}</div>
          <div class="notification-description">{{ notificationDetail.description }}</div>
          
          <div v-if="notificationDetail.image" class="notification-image">
            <img :src="notificationDetail.image" alt="Notification Image" />
          </div>
        </div>
        
        <div class="notification-meta">
          <small>Sent from {{ notificationDetail?.venueName ?? "NightPay" }} - {{ formatDateTime(notificationDetail.timeStamp) }}</small>
        </div>
      </div>

      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-value" :class="notificationDetail.deleted ? 'stat-deleted' : 'stat-sent'">
            {{ notificationDetail.deleted ? 'Deleted' : 'Sent' }}
          </div>
          <div class="stat-label">Status</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ notificationDetail.openCount }}</div>
          <div class="stat-label">Opens</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ notificationDetail.readCount }}</div>
          <div class="stat-label">Reads</div>
        </div>
      </div>

      <Button 
        v-if="!notificationDetail.deleted" 
        @click="deleteNotification(notificationDetail.id)" 
        class="delete-notification-button"
        severity="danger"
        icon="fa fa-trash"
        label="Delete from all user inboxes"
      />
    </div>
  </Sidebar>

  <div class="page-container">
    <div class="max-w-7xl mx-auto p-6">
      <!-- Header Section -->
      <div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
        <div class="flex align-items-center justify-content-between">
          <div class="flex align-items-center gap-4">
            <div class="header-icon-container">
              <i class="fa fa-bell text-white text-2xl"></i>
            </div>
            <div>
              <h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">{{ pageTitle }}</h1>
              <p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
            </div>
          </div>
          <div class="flex gap-3">
            <Button
              @click="navigateToRecurringNotifications"
              class="recurring-notifications-button"
            >
              <template #default>
                <div class="button-content">
                  <div class="button-icon-container">
                    <i class="pi pi-clock text-white"></i>
                  </div>
                  <span class="button-label">Recurring Notifications</span>
                </div>
              </template>
            </Button>
          </div>
        </div>
      </div>

      <Loading
        v-if="loading"
        :full="false"
        :title="'Loading ' + pageTitle + '...'"
      />

      <!-- Main Content -->
      <div v-else class="bg-white border-round-2xl shadow-1 overflow-hidden">
        <!-- Table Header -->
        <div class="table-header-section">
          <div class="flex align-items-center gap-3">
            <div class="bg-blue-100 p-2 border-round-lg">
              <i class="fa fa-list text-blue-600"></i>
            </div>
            <h2 class="text-xl font-semibold text-gray-800 m-0">Notifications Overview ({{ notifications.length }})</h2>
          </div>
        </div>

        <DataTable
          @row-click="onNotificationSelected"
          :value="notifications"
          dataKey="id"
          class="modern-datatable"
          :rowHover="true"
          responsiveLayout="scroll"
          stripedRows
        >
          <template #empty>
            <div class="text-center py-8">
              <i class="fa fa-inbox text-gray-400 text-4xl mb-3"></i>
              <p class="text-gray-500 text-lg m-0">No notifications found</p>
              <p class="text-gray-400 text-sm m-0 mt-2">Notifications will appear here once sent</p>
            </div>
          </template>
          <template #loading>
            <div class="text-center py-6">
              <i class="fa fa-spinner fa-spin text-blue-500 text-2xl mb-2"></i>
              <p class="text-gray-600 m-0">Loading notifications...</p>
            </div>
          </template>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-image text-gray-600"></i>
                <span class="font-semibold text-gray-700">Logo</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex align-items-center gap-3 py-2">
                <div class="venue-logo-table">
                  <img :src="data.venueLogoUrl ?? npLogo" alt="Venue Logo" />
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-tag text-gray-600"></i>
                <span class="font-semibold text-gray-700">Title</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex align-items-center gap-3 py-2">
                <div class="bg-blue-100 p-2 border-round-lg">
                  <i class="fa fa-bell text-blue-600"></i>
                </div>
                <div>
                  <p class="font-semibold text-gray-800 m-0">{{ data?.title }}</p>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-building text-gray-600"></i>
                <span class="font-semibold text-gray-700">Venue</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div class="status-badge venue-badge">
                  <i class="fa fa-store text-sm"></i>
                  <span>{{ data.venueName ?? 'NightPay' }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-calendar text-gray-600"></i>
                <span class="font-semibold text-gray-700">Sent Date</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div class="status-badge date-badge">
                  <i class="fa fa-clock text-sm"></i>
                  <span>{{ formatDateTime(data.created) }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-eye text-gray-600"></i>
                <span class="font-semibold text-gray-700">Opens</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div class="status-badge opens-badge">
                  <i class="fa fa-eye text-sm"></i>
                  <span>{{ data.openCount || 0 }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-circle text-gray-600"></i>
                <span class="font-semibold text-gray-700">Status</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div
                  :class="data.deleted ? 'status-deleted' : 'status-sent'"
                  class="status-badge"
                >
                  <i :class="data.deleted ? 'fa fa-times-circle' : 'fa fa-check-circle'" class="text-sm"></i>
                  <span>{{ data.deleted ? 'Deleted' : 'Sent' }}</span>
                </div>
              </div>
            </template>
          </Column>

     
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import { useRouter } from 'vue-router';
import useFormatDate from '../../utils/useFormatDate';
import npLogo from '@/assets/logo_square.png'


export default {
	components: {},
	setup() {
		const storeActionGetEntities = 'notifications/getNotificationListItems';
		const storeGetterGetEntities = 'notifications/notificationListItems';
		// // const storeActionCreateEntity = 'memberships/create'
		// const storeActionUpdateEntity = 'memberships/update';
		const storeActionDeleteEntity = 'notifications/delete';
		// const storeActionUpdatePerkConfigs = 'membershipPerks/update';
		const entityName = 'Notification';
		const pageTitle = 'App notifications';
		const pageDescription = 'Here you can manage sent app notifications';
        
        const store = useStore();
        const toast = useToast();
        const router = useRouter();

        const notifications = ref([])
        const notificationDetail = ref()

        const loading = ref(false)

        const showNotificationDetailDialog = ref(false)


        function onNotificationSelected(notification){
            notificationDetail.value = notification.data
            showNotificationDetailDialog.value = true;

        }

        function formatDateTime(date){
            return useFormatDate().formatDateTime(date) ?? "-"
        }

        function navigateToRecurringNotifications() {
            router.push('/recurring-notifications');
        }

        async function deleteNotification(notificationId){

            await store.dispatch(storeActionDeleteEntity, {notificationId})
            .then(()=>{
                toast.add({ severity: 'success', summary: 'Success', detail: 'Notification was deleted', life: 3000 });
                showNotificationDetailDialog.value = false
            })
            .catch((err)=>{
                console.log("HEP")
                toast.add({ severity: 'error', summary: 'Error', detail: 'Could delete notification' + ': ' + err, life: 3000 });
            })
         
            loadRequiredData();
        }

        async function loadRequiredData(){

            // Load notifications
            loading.value = true;
            await store.dispatch(storeActionGetEntities)
            .then(()=> {
                const loadedNotifications = store.getters[storeGetterGetEntities]
                notifications.value = loadedNotifications
                console.log(notifications.value)
            })
            .catch((err)=>{
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not get notifications' + ': ' + err, life: 3000 });
            })
            .finally(()=>loading.value = false)
           
		
        }

        onMounted(()=>{
            loadRequiredData()

        })

		return {
			entityName,
			pageTitle,
			pageDescription,

            notifications,
            notificationDetail,
            loading,
            showNotificationDetailDialog,

            npLogo,

            formatDateTime,

            onNotificationSelected,
            deleteNotification,
            navigateToRecurringNotifications
		};
	},
};
</script>

<style scoped>
/* Fixed background that doesn't scroll */
.page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
  background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table header section */
.table-header-section {
  background: linear-gradient(90deg, #f9fafb 0%, #f1f5f9 100%);
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

/* Venue logo in table */
.venue-logo-table {
  width: 4rem;
  height: 4rem;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.venue-logo-table img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Status badge base styling */
.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  font-weight: 500;
  text-align: center;
  min-width: max-content;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  color: white;
  transition: all 0.2s ease;
}

.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Specific badge colors */
.venue-badge {
  background: linear-gradient(135deg, #2dd4bf 0%, #06b6d4 100%);
}

.date-badge {
  background: linear-gradient(135deg, #fbbf24 0%, #f97316 100%);
}

.opens-badge {
  background: linear-gradient(135deg, #c084fc 0%, #6366f1 100%);
}

.status-sent {
  background: linear-gradient(135deg, #4ade80 0%, #10b981 100%);
}

.status-deleted {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

/* Action buttons */
.action-button {
  padding: 0.5rem !important;
  width: 2.5rem !important;
  height: 2.5rem !important;
  border-radius: 0.5rem !important;
  border: none !important;
  transition: all 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.view-button {
  background: #3b82f6 !important;
  color: white !important;
}

.view-button:hover {
  background: #2563eb !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3) !important;
}

/* Notification Detail Sidebar Styling */
.notification-detail-sidebar {
  width: 28rem !important;
}

.notification-detail-content {
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.detail-header {
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1.5rem;
}

.detail-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;
}

.detail-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 400;
}

.venue-logo-container {
  display: flex;
  justify-content: center;
}

.venue-logo {
  width: 12rem;
  height: auto;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.notification-preview {
  flex: 1;
}

.preview-content {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  min-height: 9rem;
}

.notification-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  word-wrap: break-word;
}

.notification-description {
  color: #4b5563;
  margin-bottom: 1rem;
  word-wrap: break-word;
  line-height: 1.6;
}

.notification-image {
  border-radius: 0.5rem;
  overflow: hidden;
  margin: 0 auto;
  max-width: 12rem;
}

.notification-image img {
  width: 100%;
  height: auto;
}

.notification-meta {
  color: #9ca3af;
  font-size: 0.875rem;
}

.stats-section {
  display: flex;
  gap: 1rem;
  justify-content: space-around;
  padding: 1.5rem 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.stat-card {
  text-align: center;
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.stat-sent {
  color: #10b981;
}

.stat-deleted {
  color: #ef4444;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.delete-notification-button {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  border: none !important;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
  width: 100% !important;
}

.delete-notification-button:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-1px) !important;
}

/* Custom styling for modern datatable */
:deep(.modern-datatable) {
  border: none;
  border-radius: 0;
}

:deep(.modern-datatable .p-datatable-header) {
  background: transparent;
  border: none;
  padding: 0;
}

:deep(.modern-datatable .p-datatable-thead > tr > th) {
  background: transparent;
  border: none;
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

:deep(.modern-datatable .p-datatable-tbody > tr) {
  background: white;
  border: none;
  transition: all 0.2s ease;
  cursor: pointer;
}

:deep(.modern-datatable .p-datatable-tbody > tr:hover) {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

:deep(.modern-datatable .p-datatable-tbody > tr > td) {
  border: none;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

:deep(.modern-datatable .p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

/* Striped rows with subtle styling */
:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even)) {
  background: #fdfdfe;
}

:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even):hover) {
  background: #f1f5f9;
}

/* Loading and empty state styling */
.text-center i {
  display: block;
  margin: 0 auto;
}

/* Responsive improvements */
@media (max-width: 768px) {
  :deep(.modern-datatable .p-datatable-thead > tr > th),
  :deep(.modern-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem 1rem;
  }
  
  .max-w-7xl {
    max-width: 100%;
  }
  
  .p-6 {
    padding: 1rem;
  }
  
  .status-badge {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
  
  .notification-detail-sidebar {
    width: 100% !important;
  }
  
  .venue-logo {
    width: 8rem;
  }
  
  .stats-section {
    flex-direction: column;
    gap: 1.5rem;
  }
}

/* Smooth scrolling for responsive table */
:deep(.p-datatable-wrapper) {
  border-radius: 0;
}

/* Ensure proper shadow styling */
.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* Sidebar styling overrides */
:deep(.p-sidebar-md) {
  width: 28rem !important;
}

:deep(.notification-detail-sidebar .p-sidebar) {
  background: white;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Recurring Notifications Button Styling */
.recurring-notifications-button {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
  border: none !important;
  padding: 0.75rem 1.25rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.75rem !important;
}

.recurring-notifications-button:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-1px) !important;
}

.button-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.button-icon-container {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.button-icon-container i {
  font-size: 1rem;
}

.button-label {
  font-weight: 600;
  color: white;
  white-space: nowrap;
}

/* Responsive button styling */
@media (max-width: 768px) {
  .recurring-notifications-button {
    padding: 0.625rem 1rem !important;
  }

  .button-content {
    gap: 0.5rem;
  }

  .button-icon-container {
    padding: 0.375rem;
  }

  .button-label {
    font-size: 0.875rem;
  }
}
</style>